import {
  Box,
  createStyles,
  Divider,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import classNames from 'classnames'
import React, { ReactElement } from 'react'
import {
  Datagrid,
  EditButton,
  FunctionField,
  Identifier,
  List,
  Record,
  ReduxState,
  ReferenceField,
  ResourceContextProvider,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  useTranslate,
  useShowContext,
  Pagination,
  BooleanInput,
  SelectInput,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Authority } from '../../../core/auth/Authority'
import { CompanyDto } from '../../../core/dto/company.dto'
import { RoleDto } from '../../../core/dto/role/role.dto'
import { ResourceName } from '../../../core/ResourceName'
import RelationTab from '../../common/RelationTab'
import relationTabFilter from '../../common/RelationTabFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { DeviceBaseDto } from '../../../core/dto/device/common/device-base/device-base.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { getDeviceShowLink } from '../../../core/devices/devices.utils'
import { getDeviceIcon } from '../../../core/devices/devices.components'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'

const useStyles = makeStyles((theme: any) =>
  createStyles({
    show: {
      width: `calc(100vw - ${theme?.sidebar?.closedWidth}px - 34px)`,
    },
    showSideOpen: {
      maxWidth: `calc(100vw - ${theme?.sidebar?.width}px - 34px)`,
    },
    showXSmall: {
      width: '100vw',
    },
  }),
)

interface CompanyTitleProps extends TitleProps {
  record?: CompanyDto
}

const ShowTitle = (data: CompanyTitleProps) => {
  const { record: company } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.roles.titles.show')}: {company?.id}. {company?.name}
    </span>
  )
}

const RoleUsers = () => {
  const { record: role, basePath } = useShowContext()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <ResourceContextProvider value={ResourceName.USERS}>
        <List
          title=" "
          actions={false}
          exporter={false}
          basePath={basePath}
          filter={{ roleId: role?.id }}
          filters={undefined}
          perPage={20}
          pagination={
            <Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />
          }
          bulkActionButtons={false}
        >
          <Datagrid
            rowClick={(id: Identifier) => `/${ResourceName.USERS}/${id}/show`}
          >
            <TextField source="id" label="ID" />
            <TextField source="userName" />
            {hasAuthority(Authority.VIEW_COMPANIES) && (
              <ReferenceField
                source="companyId"
                reference={ResourceName.COMPANIES}
                link={(record: Record) =>
                  `/${ResourceName.COMPANIES}/${record.companyId}/show`
                }
              >
                <FunctionField<CompanyDto>
                  source="name"
                  render={(company?: CompanyDto) =>
                    `${company?.id}. ${company?.name}`
                  }
                />
              </ReferenceField>
            )}
          </Datagrid>
        </List>
      </ResourceContextProvider>
    </>
  )
}

const GeneralActions = () => {
  const { record: role } = useShowContext<RoleDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <EditButton
          basePath={`/${ResourceName.ROLES}`}
          record={role}
          variant="contained"
          style={{ margin: '2px' }}
          disabled={!hasAuthority(Authority.EDIT_ROLES) || role?.isPredefined}
        />
      </Box>
    </>
  )
}

const RoleTabs = ({ ...props }) => {
  const hasAuthority = useHasAuthority()
  const { record: role } = useShowContext<RoleDto>()
  const translate = useTranslate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const DeviceTypeOption = ({
    record,
  }: {
    record?: { id?: string; name?: string }
  }) => (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {getDeviceIcon(record?.id as DeviceTypes)}
      <span style={{ marginLeft: 8 }}>{record?.name}</span>
    </span>
  )

  const permissionsFilter = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      resource: ResourceName.ROLES,
    })

  const parentsFilter = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      source: 'filterChildRoleId',
      resource: ResourceName.ROLES,
    })

  const childrenFilter = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      source: 'filterParentRoleId',
      resource: ResourceName.ROLES,
    })

  const devicesFilter = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [
        <TextInput source="name" alwaysOn />,
        <BooleanInput
          label="common.related-records.actions.attached-only"
          source="filterRoleId"
          alwaysOn
        />,
        hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) ? (
          <FilteredReferenceInput
            label="resources.devices-base.filters.categoryId"
            source="categoryIdWithDescendants"
            reference={ResourceName.DEVICE_CATEGORIES}
            sort={{ field: 'name', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            filterSource="search"
            selectWithPaginationInputProps={{
              optionText: 'hierarchyString',
              showFilter: true,
            }}
            alwaysOn
          />
        ) : (
          <></>
        ),
        <SelectInput
          label="resources.devices-base.filters.deviceType"
          source="limitDeviceType"
          choices={Object.keys(DeviceTypes).map((value) => ({
            id: value,
            name: translate(`resources.enums.DeviceTypes.${value}`),
          }))}
          optionText={<DeviceTypeOption />} // pass element, RA clones it and injects `record`
          optionValue="id"
          alwaysOn
        />,
      ],
      source: 'filterRoleId',
      resource: ResourceName.DEVICES_BASE,
      disableDefaultAttachedOnlyButton: true,
    })

  return (
    <TabbedShowLayout
      {...props}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...props}
        />
      }
    >
      <Tab label="resources.roles.tabs.general">
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {role?.description && <TextField source="description" />}
        <FunctionField<RoleDto>
          source="isPredefined"
          render={(record?: RoleDto) =>
            record?.isPredefined ? (
              <div>{translate('const.yes')}</div>
            ) : (
              <div>{translate('const.no')}</div>
            )
          }
        />
        <GeneralActions />
      </Tab>
      {hasAuthority(Authority.VIEW_ROLES) && (
        <Tab path="permissions" label="resources.roles.tabs.permissions">
          <>
            <RelationTab<RoleDto>
              resource={ResourceName.PERMISSIONS}
              source="permissionsIds"
              mode={
                hasAuthority(Authority.EDIT_ROLES) && !role?.isPredefined
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachPermissions"
              detachMethod="detachPermissions"
              attachRequestPayload={(r, ids) => ({
                roleId: r.id,
                permissionsIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                roleId: r.id,
                permissionsIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterRoleId === true
              }
              filters={permissionsFilter() as ReactElement[]}
              filterDefaultValues={{
                roleId: role?.id,
                filterRoleId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_USERS) && (
        <Tab label="resources.roles.tabs.users" path="users">
          <RoleUsers />
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_ROLES) && (
        <Tab path="parents" label="resources.roles.tabs.parents">
          <>
            <RelationTab<RoleDto>
              resource={ResourceName.ROLES}
              source="parentRolesIds"
              mode={
                hasAuthority(Authority.EDIT_ROLES) && !role?.isPredefined
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachParentRolesToRole"
              detachMethod="detachParentRolesFromRole"
              attachRequestPayload={(r, ids) => ({
                roleId: r.id,
                parentRolesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                roleId: r.id,
                parentRolesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterChildRoleId === true
              }
              filters={parentsFilter() as ReactElement[]}
              filterDefaultValues={{
                childRoleId: role?.id,
                filterChildRoleId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <ReferenceField
                source="id"
                label="resources.roles.fields.name"
                reference={ResourceName.ROLES}
                link="show"
              >
                <FunctionField<RoleDto>
                  source="name"
                  render={(record?: RoleDto) =>
                    `${record?.id}. ${record?.name}`
                  }
                />
              </ReferenceField>
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_ROLES) && (
        <Tab path="children" label="resources.roles.tabs.children">
          <>
            <RelationTab<RoleDto>
              resource={ResourceName.ROLES}
              source="childRolesIds"
              mode={
                hasAuthority(Authority.EDIT_ROLES) && !role?.isPredefined
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachChildRolesToRole"
              detachMethod="detachChildRolesFromRole"
              attachRequestPayload={(r, ids) => ({
                roleId: r.id,
                childRolesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                roleId: r.id,
                childRolesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterParentRoleId === true
              }
              filters={childrenFilter() as ReactElement[]}
              filterDefaultValues={{
                parentRoleId: role?.id,
                filterParentRoleId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <ReferenceField
                source="id"
                label="resources.roles.fields.name"
                reference={ResourceName.ROLES}
                link="show"
              >
                <FunctionField<RoleDto>
                  source="name"
                  render={(record?: RoleDto) =>
                    `${record?.id}. ${record?.name}`
                  }
                />
              </ReferenceField>
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_DEVICES_BASE) && (
        <Tab path="devices" label="resources.roles.tabs.devices">
          <>
            <RelationTab<RoleDto>
              resource={ResourceName.DEVICES_BASE}
              source="permittedDevicesIds"
              mode={
                hasAuthority(Authority.EDIT_ROLES) && !role?.isPredefined
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachPermittedDevicesToRole"
              detachMethod="detachPermittedDevicesFromRole"
              attachRequestPayload={(r, ids) => ({
                roleId: r.id,
                permittedDevicesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                roleId: r.id,
                permittedDevicesIds: ids,
              })}
              relationsRequestMethod="getRolePermittedDevices"
              relationsRequestPayload={(r) => ({ roleId: r.id })}
              refetchListAfterChange={(filters) =>
                filters?.filterRoleId === true
              }
              filters={devicesFilter() as ReactElement[]}
              filterDefaultValues={{
                roleId: role?.id,
                filterRoleId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
              disableAttachAll
            >
              <TextField label="ID" source="id" />
              <ReferenceField
                source="id"
                label="resources.devices-base.fields.deviceType"
                reference={ResourceName.DEVICES_BASE}
                link={false}
              >
                <FunctionField<DeviceBaseDto>
                  render={(record?: Record) => (
                    <Box display="flex" alignItems="center">
                      {getDeviceIcon(record?.deviceType as DeviceTypes)}
                      <span style={{ marginLeft: 5 }}>
                        {translate(
                          `resources.devices-base.types.${record?.deviceType}`,
                        )}
                      </span>
                    </Box>
                  )}
                />
              </ReferenceField>
              <ReferenceField
                source="id"
                label="resources.devices-base.fields.name"
                reference={ResourceName.DEVICES_BASE}
                link={(record?: Record | null) => {
                  if (!record || !record.deviceType || !record.id) return ''
                  const deviceLink = getDeviceShowLink(
                    record?.deviceType as DeviceTypes,
                    record.id,
                  )
                  return typeof deviceLink === 'string' ? deviceLink : ''
                }}
              >
                <TextField
                  label="resources.devices-base.fields.name"
                  source="name"
                />
              </ReferenceField>
            </RelationTab>
          </>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const RoleShow = (props: ShowProps) => {
  const classes = useStyles()

  const sidebarOpen = useSelector(
    (state: ReduxState) => state.admin.ui.sidebarOpen,
  )

  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Show
      {...props}
      actions={false}
      title={<ShowTitle />}
      className={
        !xSmallScreen
          ? classNames(classes.show, sidebarOpen && classes.showSideOpen)
          : classes.showXSmall
      }
    >
      <RoleTabs />
    </Show>
  )
}
